// Route Summary Cards Component

import React from 'react';

interface RouteSummaryCardsProps {
  selectedRoute: any | null;
  routes: any[];
  selectedCustomersCount: number;
  selectedVehicles: any[];
  solutionStats: {
    vehiclesUsed: number;
    totalTime: number;
    totalDistance: number;
  };
}

export const RouteSummaryCards: React.FC<RouteSummaryCardsProps> = ({
  selectedRoute,
  routes,
  selectedCustomersCount,
  selectedVehicles,
  solutionStats
}) => {
  const getVehicleColor = (vehicleId: number): string => {
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
    return colors[(vehicleId - 1) % colors.length];
  };

  const getVehicleDisplayName = (vehicleId: number): string => {
    const vehicle = selectedVehicles.find(v => v.id === vehicleId);
    return vehicle ? vehicle.name : `Araç ${vehicleId}`;
  };

  if (selectedRoute) {
    const valhallaSummary = selectedRoute.valhalla_summary;
    const vroomSummary = selectedRoute.vroom_summary;

    // Get start and end times from route_sequence
    const startTime = selectedRoute.start_time_formatted ||
                     selectedRoute.route_sequence?.[0]?.arrival_time_formatted || '-';
    const endTime = selectedRoute.end_time_formatted ||
                   selectedRoute.route_sequence?.[selectedRoute.route_sequence.length - 1]?.arrival_time_formatted || '-';

    return (
      <>
        <div className="summary-card">
          <i className="pi pi-truck" style={{ color: getVehicleColor(selectedRoute.vehicleId || selectedRoute.vehicle_id || 0) }}></i>
          <div className="summary-card-content">
            <span className="summary-label">Araç</span>
            <span className="summary-value">{getVehicleDisplayName(selectedRoute.vehicleId || selectedRoute.vehicle_id || 0)}</span>
          </div>
        </div>
        <div className="summary-card">
          <i className="pi pi-users"></i>
          <div className="summary-card-content">
            <span className="summary-label">Müşteri</span>
            <span className="summary-value">{selectedRoute.customers_count || selectedRoute.route_sequence?.filter((s: any) => s.location_type === 'CUSTOMER' || s.location_type === 'customer').length || 0}</span>
          </div>
        </div>
        <div className="summary-card">
          <i className="pi pi-calendar"></i>
          <div className="summary-card-content">
            <span className="summary-label">Başlangıç</span>
            <span className="summary-value">{startTime}</span>
          </div>
        </div>
        <div className="summary-card">
          <i className="pi pi-calendar"></i>
          <div className="summary-card-content">
            <span className="summary-label">Bitiş</span>
            <span className="summary-value">{endTime}</span>
          </div>
        </div>
        <div className="summary-card">
          <i className="pi pi-box"></i>
          <div className="summary-card-content">
            <span className="summary-label">Yük</span>
            <span className="summary-value">{selectedRoute.total_demand || 0} kg</span>
          </div>
        </div>

        {/* Valhalla Summary Section */}
        {valhallaSummary && (
          <>
            <div className="summary-section-header" style={{ marginTop: '16px', marginBottom: '8px' }}>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Valhalla (Gerçek Yol Verisi)</span>
            </div>
            <div className="summary-card">
              <i className="pi pi-map-marker" style={{ color: '#3b82f6' }}></i>
              <div className="summary-card-content">
                <span className="summary-label">Mesafe</span>
                <span className="summary-value">{valhallaSummary.total_distance_km?.toFixed(2) || 0} km</span>
              </div>
            </div>
            <div className="summary-card">
              <i className="pi pi-clock" style={{ color: '#3b82f6' }}></i>
              <div className="summary-card-content">
                <span className="summary-label">Süre</span>
                <span className="summary-value">{valhallaSummary.total_time_formatted || '-'}</span>
              </div>
            </div>
          </>
        )}

        {/* VROOM Summary Section */}
        {vroomSummary && (
          <>
            <div className="summary-section-header" style={{ marginTop: '16px', marginBottom: '8px' }}>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>VROOM (Optimizasyon)</span>
            </div>
            <div className="summary-card">
              <i className="pi pi-map-marker" style={{ color: '#10b981' }}></i>
              <div className="summary-card-content">
                <span className="summary-label">Mesafe</span>
                <span className="summary-value">{(vroomSummary.distance / 1000)?.toFixed(2) || 0} km</span>
              </div>
            </div>
            <div className="summary-card">
              <i className="pi pi-clock" style={{ color: '#10b981' }}></i>
              <div className="summary-card-content">
                <span className="summary-label">Toplam Süre</span>
                <span className="summary-value">{Math.round((vroomSummary.duration || 0) / 60)} dk</span>
              </div>
            </div>
            <div className="summary-card">
              <i className="pi pi-user" style={{ color: '#10b981' }}></i>
              <div className="summary-card-content">
                <span className="summary-label">Servis Süresi</span>
                <span className="summary-value">{Math.round((vroomSummary.service || 0) / 60)} dk</span>
              </div>
            </div>
            {vroomSummary.waiting_time > 0 && (
              <div className="summary-card">
                <i className="pi pi-pause" style={{ color: '#f59e0b' }}></i>
                <div className="summary-card-content">
                  <span className="summary-label">Bekleme Süresi</span>
                  <span className="summary-value">{Math.round(vroomSummary.waiting_time / 60)} dk</span>
                </div>
              </div>
            )}
          </>
        )}
      </>
    );
  }

  return (
    <>
      <div className="summary-card">
        <i className="pi pi-map"></i>
        <div className="summary-card-content">
          <span className="summary-label">Toplam Rota</span>
          <span className="summary-value">{routes.length}</span>
        </div>
      </div>
      <div className="summary-card">
        <i className="pi pi-users"></i>
        <div className="summary-card-content">
          <span className="summary-label">Müşteri</span>
          <span className="summary-value">{selectedCustomersCount}</span>
        </div>
      </div>
      <div className="summary-card">
        <i className="pi pi-truck"></i>
        <div className="summary-card-content">
          <span className="summary-label">Araç</span>
          <span className="summary-value">{solutionStats.vehiclesUsed}</span>
        </div>
      </div>
      <div className="summary-card">
        <i className="pi pi-clock"></i>
        <div className="summary-card-content">
          <span className="summary-label">Toplam Süre</span>
          <span className="summary-value">{Math.round(solutionStats.totalTime / 60)} dk</span>
        </div>
      </div>
      <div className="summary-card">
        <i className="pi pi-map-marker"></i>
        <div className="summary-card-content">
          <span className="summary-label">Toplam Mesafe</span>
          <span className="summary-value">{solutionStats.totalDistance.toFixed(1)} km</span>
        </div>
      </div>
    </>
  );
};
